import { CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InstallmentsSummaryProps {
  totalInstallments: number;
  balance: number;
  currency?: 'USD' | 'BRL';
  warnings?: string[];
}

const formatCurrency = (value: number, currency = 'USD') =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);

export function InstallmentsSummary({
  totalInstallments,
  balance,
  currency = 'USD',
  warnings = [],
}: InstallmentsSummaryProps) {
  const difference = Math.round((totalInstallments - balance) * 100) / 100;
  const isValid = Math.abs(difference) < 0.01;

  return (
    <div className="space-y-2">
      <div className={cn(
        "p-3 rounded-lg border space-y-2",
        isValid 
          ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700" 
          : "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700"
      )}>
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Total das parcelas:</span>
          <span className="font-medium">{formatCurrency(totalInstallments, currency)}</span>
        </div>
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Saldo a renegociar:</span>
          <span className="font-medium">{formatCurrency(balance, currency)}</span>
        </div>
        <div className="flex justify-between items-center pt-1 border-t border-current/10">
          <span className="text-sm text-muted-foreground">Diferença:</span>
          <span className={cn(
            "flex items-center gap-1 font-semibold",
            isValid ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"
          )}>
            {isValid ? (
              <>
                <CheckCircle2 className="h-4 w-4" />
                Confere
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4" />
                {formatCurrency(Math.abs(difference), currency)}
                {difference > 0 ? ' a mais' : ' a menos'}
              </>
            )}
          </span>
        </div>
      </div>

      {warnings.length > 0 && (
        <div className="p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
          {warnings.map((warning, index) => (
            <div key={index} className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-3 w-3 flex-shrink-0" />
              <span>{warning}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
